<template>
  <div class="news">
    <h3>
      {{ $t('DASHBOARD.NEWS') }}
    </h3>
    <div v-if="feedItems && feedItems.length" class="feed">
      <ul>
        <li v-for="item in feedItems" :key="item.url">
          <a
            v-trackClickEvent="{
              name: 'blog-post-clicked',
              details: { pos: item.pos },
            }"
            class="feed-item"
            :href="item.url"
            target="_blank"
          >
            <div class="feed-item-data">
              <div class="top">
                <div class="preview-image">
                  <div class="preview-image-wrapper">
                    <img
                      v-if="item.previewImage"
                      :src="item.previewImage"
                      alt="Blog Post Thumbnail"
                    />
                    <Icon icon="blog_placeholder" />
                  </div>
                </div>
                <div class="feed-content">
                  <div class="meta">
                    <span class="date text-sm">{{
                      formatDate(item.date) || '&nbsp;'
                    }}</span>
                    <span v-if="item.author" class="text-grey text-sm">
                      {{ ` - ${item.author}` }}</span
                    >
                  </div>
                  <h4 class="text-truncate" v-html="item.title"></h4>
                  <p
                    class="feed-summary text-grey text-sm"
                    v-html="item.description"
                  ></p>
                </div>
              </div>
              <div class="bottom">
                <MarketplaceLogo
                  v-if="partnerType !== 'SHOP' && item.pos === 'MP'"
                />
                <SpreadshopLogo
                  v-if="partnerType !== 'MP' && item.pos === 'SHOP'"
                />
              </div>
            </div>
            <div class="more-link">
              <Icon icon="chevron"></Icon>
            </div>
          </a>
        </li>
      </ul>
    </div>
    <div v-else-if="!feedItems" class="loader">
      <img src="/images/loader.svg" class="sprd-loader" />
    </div>
    <p v-else class="text-sm error" v-html="$t('DASHBOARD.NEWS_ERROR')"></p>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import date from 'commons/date/date';
import localeService from 'commons/localeService/localeService';
import MarketplaceLogo from 'commons/logos/MarketplaceLogo.vue';
import SpreadshopLogo from 'commons/logos/SpreadshopLogo.vue';
import { fetchBlogPosts } from '@spreadgroup/common-partner-lib/blog';
import type {
  BlogPost,
  FetchBlogPostsParams,
} from '@spreadgroup/common-partner-lib/blog';

declare const SPRD: { PLATFORM: 'EU' | 'NA' };

const props = withDefaults(defineProps<{ count?: number }>(), { count: 10 });

const store = useStore();
const partnerType = computed<FetchBlogPostsParams['partnerType']>(
  () => store.getters['user/partnerType'],
);

const feedItems = ref<BlogPost[] | null>(null);

function formatDate(isoDate: string | null): string | null {
  return isoDate ? date(isoDate, 'longDate') : null;
}

onMounted(async () => {
  try {
    feedItems.value = await fetchBlogPosts({
      partnerType: partnerType.value,
      platform: SPRD.PLATFORM,
      language: localeService.getLanguage(),
      limit: props.count,
    });
  } catch {
    feedItems.value = [];
  }
});
</script>

<style lang="scss" scoped>
@import 'src/scss/animations';
@import 'src/scss/styleguide/colors';
@import 'src/scss/constants';
@import 'src/scss/utils';

.news {
  padding: 24px;
  border-radius: $border-radius-medium;
  background-color: $white;
  overflow: hidden;

  & > h3 {
    margin: 0 0 10px;
  }
}

.loader {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 220px 0;

  img {
    height: 100px;
    width: 100px;
  }
}

.error {
  text-align: center;
  margin: 16px 0 0;
}

ul {
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  margin: 0 -24px -24px -24px;
}

li {
  list-style: none;
  flex-grow: 1;
  flex-basis: 0;
  width: 100%;
  min-width: 460px;
}

.feed-item {
  display: flex;
  align-items: center;
  padding: 24px;
  color: $grey80;

  .feed-item-data {
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: hidden;

    .top {
      display: flex;
    }

    .bottom {
      margin-left: 106px;
      margin-top: 8px;

      img {
        height: 12px;
        margin-right: 16px;
      }
    }
  }

  .preview-image {
    flex-shrink: 0;
    .preview-image-wrapper {
      display: flex;
      height: 90px;
      width: 90px;
      justify-content: center;
      align-items: center;
      overflow: hidden;
      position: relative;
      border-radius: $border-radius-medium;
      background-color: $grey5;

      img {
        position: absolute;
        height: 100%;
        top: 0;
        left: -40%;
        z-index: 1;
      }

      svg.icon {
        height: 24px;
        width: 24px;
      }
    }
  }

  .feed-content {
    padding-left: 16px;
    flex-grow: 1;
    flex: 1;
    overflow: hidden;

    .meta {
      .date {
        font-weight: bold;
      }
    }

    h4 {
      margin: 8px 0;
    }

    .feed-summary {
      display: flex;
      align-items: center;

      // TODO: can this be removed or is can the v-html contain p tags?
      p {
        margin: 0;

        a {
          display: none;
        }
      }

      p:not(:first-child) {
        display: none;
      }
    }
  }

  .more-link {
    padding-left: 5px;
    margin-left: auto;

    .icon {
      height: 16px;
      width: 16px;
      color: $grey20;
    }
  }

  &:hover {
    .more-link .icon,
    h4 {
      color: $pa-color-main;
    }
  }
}
</style>
